import { useEffect, useState } from 'react';
import { FiUsers, FiInbox, FiCheckCircle, FiClock, FiLock, FiAward, FiActivity } from 'react-icons/fi';
import { useAuth } from '../../contexts/AuthContext';
import { getEstabelecimentoAtualId } from '../../lib/estabelecimento';
import { PortalPage } from '../../components/layout/PortalPage';
import { SectionCard } from '../../components/layout/SectionCard';
import { Spinner } from '../../components/ui/Spinner';
import { Button } from '../../components/ui/Button';
import {
  conselhoService, type Vaga, type MinhaCandidatura, type Aviso,
  type ConselheiroPublico, type AndamentoVaga,
} from '../../services/conselho.service';
import { FormularioPerguntas, primeiraPendente } from '../../components/FormularioPerguntas';
import { formatarDataHoraBR } from '../../lib/data';
import type { RespostasConselho } from '@ponti/shared';

function vagaEncerrada(v: Vaga): boolean {
  return !!v.encerra_em && v.encerra_em < new Date().toISOString().slice(0, 10);
}
const dataBR = (iso: string | null) => iso ? iso.split('-').reverse().join('/') : '—';
const STATUS_CAND_LABEL: Record<MinhaCandidatura['status'], string> = {
  pendente: 'Em análise', aprovada: 'Aprovada', reprovada: 'Não aprovada',
};

type Aba = 'vagas' | 'processo' | 'conselheiros' | 'minhas' | 'avisos';

export function ConselhoPage() {
  const { cidadao } = useAuth();
  const estabId = getEstabelecimentoAtualId();
  const [aba, setAba] = useState<Aba>('vagas');
  const [vagas, setVagas] = useState<Vaga[]>([]);
  const [minhas, setMinhas] = useState<MinhaCandidatura[]>([]);
  const [avisos, setAvisos] = useState<Aviso[]>([]);
  const [conselheiros, setConselheiros] = useState<ConselheiroPublico[]>([]);
  const [andamento, setAndamento] = useState<AndamentoVaga[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [candidatando, setCandidatando] = useState<string | null>(null);
  const [justificativa, setJustificativa] = useState('');
  const [respostas, setRespostas] = useState<RespostasConselho>({});
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  async function carregar() {
    if (!cidadao) return;
    const [v, m, a, co, an] = await Promise.all([
      conselhoService.listarVagasAbertas(estabId),
      conselhoService.minhasCandidaturas(cidadao.id),
      conselhoService.listarAvisos().catch(() => []),
      conselhoService.listarConselheiros(estabId).catch(() => []),
      conselhoService.andamento(estabId).catch(() => []),
    ]);
    setVagas(v); setMinhas(m); setAvisos(a); setConselheiros(co); setAndamento(an);
    setCarregando(false);
  }
  useEffect(() => { carregar(); /* eslint-disable-next-line */ }, [cidadao]);

  const membro = minhas.some((c) => c.status === 'aprovada');
  const candidaturaDe = (vagaId: string) => minhas.find((c) => c.vaga_id === vagaId);

  function iniciarCandidatura(vagaId: string) {
    setCandidatando(vagaId); setJustificativa(''); setRespostas({}); setErro(null);
  }
  async function enviarCandidatura(vaga: Vaga) {
    if (!cidadao) return;
    setErro(null);
    const pendente = primeiraPendente(vaga.perguntas ?? [], respostas);
    if (pendente) { setErro(`Responda: "${pendente.label}".`); return; }
    if (!justificativa.trim()) { setErro('Preencha a justificativa da candidatura.'); return; }
    setEnviando(true);
    try {
      await conselhoService.candidatar(estabId, vaga.id, cidadao.id, cidadao.nome_social || cidadao.nome, justificativa.trim(), respostas);
      setCandidatando(null); setJustificativa(''); setRespostas({});
      carregar();
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Falha ao enviar a candidatura.');
    } finally { setEnviando(false); }
  }

  const ABAS = ([
    { id: 'vagas', label: 'Vagas abertas', mostrar: true },
    { id: 'processo', label: 'Processo de escolha', mostrar: true },
    { id: 'conselheiros', label: 'Conselheiros', mostrar: true },
    { id: 'minhas', label: 'Minhas candidaturas', mostrar: minhas.length > 0 },
    { id: 'avisos', label: 'Avisos', mostrar: membro },
  ] satisfies { id: Aba; label: string; mostrar: boolean }[]).filter((a) => a.mostrar);

  return (
    <PortalPage
      titulo="Conselho de usuários"
      subtitulo="Participe do conselho de usuários do serviço público (Lei 13.460/2017)."
      icone={<FiUsers size={26} />}
      voltarHref="/"
      largura="larga"
    >
      {carregando ? <div className="flex justify-center py-16"><Spinner /></div> : (
        <div className="space-y-5">
          <div className="inline-flex flex-wrap gap-1 rounded-xl border border-slate-200 bg-white p-1">
            {ABAS.map((a) => (
              <button key={a.id} onClick={() => setAba(a.id)}
                className={`rounded-lg px-3.5 py-2 text-sm font-semibold transition ${aba === a.id ? 'bg-institutional text-white' : 'text-slate-600 hover:bg-slate-50'}`}>
                {a.label}
              </button>
            ))}
          </div>

          {/* VAGAS */}
          {aba === 'vagas' && (vagas.length === 0 ? (
            <Vazio icone={<FiInbox size={26} />} texto="Nenhuma vaga aberta no momento." />
          ) : vagas.map((v) => {
            const c = candidaturaDe(v.id);
            return (
              <SectionCard key={v.id} titulo={v.titulo} subtitulo={v.descricao || undefined} icone={<FiUsers size={18} />}>
                <p className="text-xs text-slate-400">
                  {v.total_vagas} vaga(s){v.encerra_em ? ` · até ${dataBR(v.encerra_em)}` : ''}
                  {(v.perguntas?.length ?? 0) > 0 ? ` · ${v.perguntas.length} pergunta(s)` : ''}
                </p>
                {c ? (
                  <div className={`mt-3 inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium ${c.status === 'aprovada' ? 'bg-emerald-50 text-emerald-700' : c.status === 'reprovada' ? 'bg-red-50 text-red-700' : 'bg-amber-50 text-amber-700'}`}>
                    {c.status === 'aprovada' ? <FiCheckCircle size={14} /> : <FiClock size={14} />} {STATUS_CAND_LABEL[c.status]}
                  </div>
                ) : vagaEncerrada(v) ? (
                  <div className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-slate-100 px-3 py-1.5 text-sm font-medium text-slate-500"><FiClock size={14} /> Inscrições encerradas</div>
                ) : candidatando === v.id ? (
                  <div className="mt-3 space-y-3 border-t border-slate-100 pt-3">
                    {(v.perguntas?.length ?? 0) > 0 && <FormularioPerguntas perguntas={v.perguntas} valores={respostas} onChange={setRespostas} />}
                    <div>
                      <label className="mb-1 block text-sm font-medium text-slate-700">Justificativa da candidatura <span className="text-red-500">*</span></label>
                      <textarea value={justificativa} onChange={(e) => setJustificativa(e.target.value)} rows={3} placeholder="Por que você quer participar do conselho?"
                        className="w-full rounded-md border-2 border-slate-200 px-3 py-2 text-sm outline-none focus:border-institutional" />
                    </div>
                    {erro && <p className="text-sm font-medium text-red-600">{erro}</p>}
                    <div className="flex gap-2">
                      <Button onClick={() => enviarCandidatura(v)} loading={enviando}>Confirmar candidatura</Button>
                      <Button variant="outline" onClick={() => { setCandidatando(null); setErro(null); }}>Cancelar</Button>
                    </div>
                  </div>
                ) : (
                  <Button className="mt-3" onClick={() => iniciarCandidatura(v.id)}>Candidatar-se</Button>
                )}
              </SectionCard>
            );
          }))}

          {/* PROCESSO DE ESCOLHA */}
          {aba === 'processo' && (
            <SectionCard titulo="Andamento do processo de escolha" icone={<FiActivity size={18} />}>
              {andamento.length === 0 ? <p className="text-sm text-slate-500">Nenhum processo cadastrado.</p> : (
                <div className="overflow-hidden rounded-xl border border-slate-200">
                  <table className="w-full text-sm">
                    <thead><tr className="bg-slate-50 text-left text-xs uppercase text-slate-500">
                      <th className="px-4 py-2.5 font-semibold">Vaga</th><th className="px-4 py-2.5 font-semibold">Situação</th>
                      <th className="px-4 py-2.5 font-semibold">Candidatos</th><th className="px-4 py-2.5 font-semibold">Aprovados</th><th className="px-4 py-2.5 font-semibold">Empossados</th>
                    </tr></thead>
                    <tbody className="divide-y divide-slate-100">
                      {andamento.map((a, i) => {
                        const encerrada = !!a.encerra_em && a.encerra_em < new Date().toISOString().slice(0, 10);
                        const situacao = !a.aberta || encerrada
                          ? (a.empossados > 0 ? 'Concluído' : 'Encerrado')
                          : (a.aprovados > 0 ? 'Em análise/aprovação' : 'Inscrições abertas');
                        return (
                          <tr key={i}>
                            <td className="px-4 py-2.5 font-medium text-slate-800">{a.vaga}</td>
                            <td className="px-4 py-2.5 text-slate-600">{situacao}{a.encerra_em ? ` · até ${dataBR(a.encerra_em)}` : ''}</td>
                            <td className="px-4 py-2.5 tabular-nums text-slate-700">{a.candidatos}</td>
                            <td className="px-4 py-2.5 tabular-nums text-slate-700">{a.aprovados}/{a.total_vagas}</td>
                            <td className="px-4 py-2.5 tabular-nums text-slate-700">{a.empossados}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
              <p className="mt-3 text-xs text-slate-400">Processo aberto ao público conforme a Lei 13.460/2017.</p>
            </SectionCard>
          )}

          {/* CONSELHEIROS EMPOSSADOS */}
          {aba === 'conselheiros' && (
            <SectionCard titulo="Conselheiros empossados" icone={<FiAward size={18} />}>
              {conselheiros.length === 0 ? <p className="text-sm text-slate-500">Nenhum conselheiro empossado ainda.</p> : (
                <ul className="divide-y divide-slate-100">
                  {conselheiros.map((m, i) => (
                    <li key={i} className="flex items-center justify-between gap-3 py-3">
                      <div>
                        <div className="font-medium text-slate-900">{m.nome}</div>
                        {m.vaga && <div className="text-xs text-slate-500">{m.vaga}</div>}
                      </div>
                      <div className="text-right text-xs text-slate-400">
                        <div>Posse: {dataBR(m.data_posse)}</div>
                        <div>Mandato até: {m.mandato_fim ? dataBR(m.mandato_fim) : 'indeterminado'}</div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </SectionCard>
          )}

          {/* MINHAS CANDIDATURAS */}
          {aba === 'minhas' && (
            <SectionCard titulo="Minhas candidaturas" icone={<FiUsers size={18} />}>
              <ul className="divide-y divide-slate-100">
                {minhas.map((c) => {
                  const v = vagas.find((vg) => vg.id === c.vaga_id);
                  return (
                    <li key={c.id} className="flex items-center justify-between gap-3 py-3">
                      <span className="text-sm text-slate-700">{v?.titulo ?? 'Vaga do conselho'}</span>
                      <span className={`rounded-lg px-2.5 py-1 text-xs font-medium ${c.status === 'aprovada' ? 'bg-emerald-50 text-emerald-700' : c.status === 'reprovada' ? 'bg-red-50 text-red-700' : 'bg-amber-50 text-amber-700'}`}>
                        {STATUS_CAND_LABEL[c.status]}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </SectionCard>
          )}

          {/* AVISOS (área restrita do membro) */}
          {aba === 'avisos' && (
            <SectionCard titulo="Área restrita — avisos do conselho" icone={<FiLock size={18} />} className="border-emerald-200">
              {avisos.length === 0 ? <p className="text-sm text-slate-500">Nenhum aviso publicado ainda.</p> : (
                <ul className="space-y-2">
                  {avisos.map((a) => (
                    <li key={a.id} className="rounded-lg border border-emerald-200 bg-emerald-50/50 p-3">
                      <div className="font-medium text-slate-900">{a.titulo}</div>
                      <p className="mt-0.5 whitespace-pre-wrap text-sm text-slate-600">{a.conteudo}</p>
                      <p className="mt-2 text-[11px] text-slate-400">
                        {formatarDataHoraBR(a.criado_em)}{a.criado_por_nome ? ` · por ${a.criado_por_nome}` : ''}
                      </p>
                    </li>
                  ))}
                </ul>
              )}
            </SectionCard>
          )}
        </div>
      )}
    </PortalPage>
  );
}

function Vazio({ icone, texto }: { icone: React.ReactNode; texto: string }) {
  return (
    <SectionCard>
      <div className="py-10 text-center">
        <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-slate-100 text-slate-400">{icone}</div>
        <p className="text-sm font-medium text-slate-700">{texto}</p>
      </div>
    </SectionCard>
  );
}
